import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { UserMenu } from './UserMenu';
import { usePermissions } from '@/hooks/usePermissions';

export interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
  disabled?: boolean;
  comingSoon?: boolean;
  requiredPermissions?: string[]; // User must have ANY of these permissions
}

export interface NavSection {
  title?: string;
  items: NavItem[];
  requiredPermissions?: string[]; // Section visible if user has ANY of these permissions
}

export interface BaseLayoutProps {
  children: React.ReactNode;
  navItems?: NavItem[]; // Legacy support
  navSections?: NavSection[]; // New sectioned navigation
  sidebarTitle: string;
  sidebarDescription: string;
  headerSubtitle: string;
  rootPath: string;
}

export function BaseLayout({
  children,
  navItems,
  navSections,
  sidebarTitle,
  sidebarDescription,
  headerSubtitle,
  rootPath
}: BaseLayoutProps) {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();

  // Close sidebar when route changes
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Convert legacy navItems to sections format for unified processing
  const sections = React.useMemo<NavSection[]>(() => {
    if (navSections) {
      return navSections;
    }
    if (navItems) {
      return [{ items: navItems }];
    }
    return [];
  }, [navItems, navSections]);

  // Filter sections and items based on permissions
  const filteredSections = React.useMemo(() => {
    return sections
      .map((section) => {
        // Filter items based on permissions
        const filteredItems = section.items.filter((item) => {
          if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
            return true;
          }
          // While permissions are loading, show items (will be filtered after load)
          if (permissionsLoading) {
            return true;
          }
          return hasAnyPermission(item.requiredPermissions as any[]);
        });

        return { ...section, items: filteredItems };
      })
      .filter((section) => {
        // Filter out empty sections
        if (section.items.length === 0) return false;
        
        // Check section-level permissions
        if (section.requiredPermissions && section.requiredPermissions.length > 0) {
          // While permissions are loading, show sections (will be filtered after load)
          if (permissionsLoading) {
            return true;
          }
          return hasAnyPermission(section.requiredPermissions as any[]);
        }
        
        return true;
      });
  }, [sections, hasAnyPermission, permissionsLoading]);

  // Flatten all items for active href detection
  const allItems = React.useMemo(() => {
    return filteredSections.flatMap((section) => section.items);
  }, [filteredSections]);

  // Determine the single active nav item by choosing the longest matching href prefix or exact match
  const activeHref = React.useMemo(() => {
    const matches = allItems.filter((item) => {
      if (item.href === rootPath) return location.pathname === rootPath;
      return location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    });
    if (matches.length === 0) return undefined as string | undefined;
    return matches.reduce((prev, cur) => (cur.href.length > prev.href.length ? cur : prev)).href;
  }, [location.pathname, allItems, rootPath]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#efede7' }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
              <img 
                src="/images/logo_moniker_cockpit.png" 
                alt="Moniker Cockpit" 
                className="h-6 object-contain"
              />
            </Link>
          </div>
          <UserMenu />
        </div>
      </header>

      <div className="flex relative">
        {/* Mobile backdrop */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-gray-900 bg-opacity-50 z-30 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar Navigation */}
        <nav 
          className={cn(
            "fixed lg:sticky top-16 z-40 h-[calc(100vh-4rem)] w-72 bg-white border-r border-gray-200 transition-transform duration-200 ease-in-out lg:translate-x-0",
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="p-6 h-full overflow-y-auto">
            {/* Mobile close button */}
            <div className="flex items-center justify-between mb-6 lg:hidden">
              <h2 className="text-lg font-semibold text-gray-900">{sidebarTitle}</h2>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Desktop title */}
            <div className="hidden lg:block mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">{sidebarTitle}</h2>
              <p className="text-sm text-muted-foreground">{sidebarDescription}</p>
            </div>

            {/* Mobile description */}
            <p className="text-sm text-muted-foreground mb-6 lg:hidden">{sidebarDescription}</p>

            <div className="space-y-6">
              {filteredSections.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  {/* Section Title (if provided) */}
                  {section.title && (
                    <div className="mb-2 px-3">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {section.title}
                      </h3>
                    </div>
                  )}

                  {/* Section Items */}
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const isActive = item.href === activeHref;
                      const isDisabled = item.comingSoon;

                      if (isDisabled) {
                        return (
                          <div
                            key={item.href}
                            className="flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg text-gray-400 cursor-not-allowed"
                          >
                            <span className="opacity-50">{item.icon}</span>
                            <span className="flex-1">{item.label}</span>
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                              Coming Soon
                            </span>
                          </div>
                        );
                      }

                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={cn(
                            'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-all',
                            isActive
                              ? 'bg-primary-50 text-primary-700 border border-primary-200 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          )}
                          onClick={() => setIsSidebarOpen(false)}
                        >
                          {item.icon}
                          <span className="flex-1">{item.label}</span>
                          {item.badge && (
                            <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="h-full">
            <div className="max-w-7xl mx-auto">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
